import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";
import type { PaymentMethod } from "./pos";

export type QuoteStatus = "ABIERTA" | "CONVERTIDA" | "ANULADA";

export const QUOTE_STATUS_LABEL: Record<QuoteStatus, string> = {
  ABIERTA: "Abierta",
  CONVERTIDA: "Convertida en venta",
  ANULADA: "Anulada",
};

export interface QuoteLineRecord {
  id: string;
  productId: string;
  quantity: string;
  unitPrice: string;
  taxRate: string;
  discountAmount: string;
  subtotal: string;
  product?: { id: string; sku: string; name: string; unit: string };
}

export interface QuoteRecord {
  id: string;
  branchId: string;
  branch?: { id: string; name: string };
  storeCustomerId: string | null;
  storeCustomer?: { id: string; name: string; documentId: string | null; phone: string | null } | null;
  customerName: string | null;
  customerPhone: string | null;
  quoteNumber: number;
  quoteDate: string;
  validUntil: string | null;
  subtotal: string;
  taxTotal: string;
  discountTotal: string;
  total: string;
  status: QuoteStatus;
  notes: string | null;
  isExpired: boolean;
  saleId: string | null;
  sale?: { id: string; saleNumber: number; saleDate: string; total: string } | null;
  createdBy?: { id: string; fullName: string } | null;
  createdAt: string;
  lines: QuoteLineRecord[];
}

export interface ListQuotesFilters {
  page?: number;
  pageSize?: number;
  storeCustomerId?: string;
  status?: QuoteStatus;
  branchId?: string;
}

/** Cotizaciones de tienda: precio estimado para un cliente, sin tocar stock ni requerir turno de caja abierto — ver quote.service.ts en el backend. */
export function useQuotes(filters: ListQuotesFilters = {}) {
  return useQuery({
    queryKey: ["store", "quotes", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<QuoteRecord[]>>("/quotes", { params: filters });
      return data;
    },
  });
}

export function useQuote(id: string | null) {
  return useQuery({
    queryKey: ["store", "quotes", "detail", id],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<QuoteRecord>>(`/quotes/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

export interface CreateQuotePayload {
  branchId: string;
  storeCustomerId?: string;
  customerName?: string;
  customerPhone?: string;
  validUntil?: string;
  notes?: string;
  lines: Array<{ productId: string; quantity: number | string }>;
}

export function useCreateQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateQuotePayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<QuoteRecord>>("/quotes", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "quotes"] }),
  });
}

export function useVoidQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<ApiSuccessResponse<QuoteRecord>>(`/quotes/${id}/void`, {});
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "quotes"] }),
  });
}

export interface ConvertQuotePayload {
  id: string;
  paymentMethod?: PaymentMethod;
  amountPaid: number | string;
  creditInstallments?: number;
  creditFirstDueDate?: string;
  creditIntervalDays?: number;
  /** Ver pos.service.ts::createSale — los precios se recalculan VIGENTES al convertir, pueden quedar por debajo del costo aunque no lo estuvieran al crear la cotización. */
  acknowledgeBelowCost?: boolean;
}

/** Convierte la cotización en una venta real del POS (requiere turno de caja abierto en la sucursal de la cotización) — ver quote.service.ts::convertQuoteToSale. */
export function useConvertQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: ConvertQuotePayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<QuoteRecord>>(`/quotes/${id}/convert`, body);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", "quotes"] });
      queryClient.invalidateQueries({ queryKey: ["store", "products"] });
      queryClient.invalidateQueries({ queryKey: ["store", "credits"] });
    },
  });
}
